import {
	DensitySettings,
	GeneralTabConfig,
	TabField,
} from "@/dentlab/src/types/tab-config.types";

export const DEFAULT_DENSITY: DensitySettings = {
	padding: 16,
	gap: 12,
	inputHeight: 48,
	fontSize: 14,
};

export const DENSITY_PRESETS: Record<string, DensitySettings> = {
	big: { padding: 16, gap: 12, inputHeight: 48, fontSize: 14 },
	medium: { padding: 10, gap: 6, inputHeight: 36, fontSize: 12 },
	small: { padding: 4, gap: 2, inputHeight: 28, fontSize: 12 },
};

export function getDensityStyles(settings: DensitySettings) {
	return {
		padding: `${settings.padding}px`,
		gap: `${settings.gap}px`,
		inputHeight: `${settings.inputHeight}px`,
	};
}

export function parseDensity(raw: unknown): DensitySettings {
	if (!raw || typeof raw !== "object") return DEFAULT_DENSITY;
	const obj = raw as Record<string, unknown>;
	return {
		padding:
			typeof obj.density_padding === "number"
				? obj.density_padding
				: DEFAULT_DENSITY.padding,
		gap:
			typeof obj.density_gap === "number"
				? obj.density_gap
				: DEFAULT_DENSITY.gap,
		inputHeight:
			typeof obj.density_input_height === "number"
				? obj.density_input_height
				: DEFAULT_DENSITY.inputHeight,
		fontSize:
			typeof obj.density_font_size === "number"
				? obj.density_font_size
				: DEFAULT_DENSITY.fontSize,
	};
}

export function normalizeGeneralTab(tab: GeneralTabConfig): GeneralTabConfig {
	const columns = tab.columns ?? 1;

	// Auto-flow only for legacy configs where no field has an explicit position
	const needsAutoFlow =
		columns > 1 &&
		tab.fields.every((f) => f.col === undefined && f.row === undefined);

	let raw: TabField[];
	if (needsAutoFlow) {
		// Auto-flow fields left-to-right, top-to-bottom
		let curRow = 0;
		let curCol = 0;
		raw = tab.fields.map((field) => {
			const span = Math.min(field.colSpan ?? 1, columns);
			if (curCol + span > columns) {
				curRow++;
				curCol = 0;
			}
			const result = {
				...field,
				col: curCol,
				row: curRow,
				colSpan: span,
			};
			curCol += span;
			if (curCol >= columns) {
				curRow++;
				curCol = 0;
			}
			return result;
		});
	} else {
		raw = tab.fields.map((field, i) => ({
			...field,
			col: field.col ?? 0,
			row: field.row ?? i,
			colSpan: Math.min(field.colSpan ?? 1, columns),
		}));
	}

	// Deduplicate: if multiple fields share the same cell, relocate duplicates
	const occupied = new Set<string>();
	let maxRow = tab.rows ?? raw.length;
	const fields = raw.map((f) => {
		const key = `${f.col},${f.row}`;
		if (!occupied.has(key)) {
			// Mark all cells this field covers
			for (let c = f.col!; c < f.col! + f.colSpan!; c++) {
				occupied.add(`${c},${f.row}`);
			}
			return f;
		}
		// Collision — find next free cell
		for (let r = 0; ; r++) {
			for (let c = 0; c <= columns - (f.colSpan ?? 1); c++) {
				let free = true;
				for (let s = 0; s < (f.colSpan ?? 1); s++) {
					if (occupied.has(`${c + s},${r}`)) {
						free = false;
						break;
					}
				}
				if (free) {
					for (let s = 0; s < (f.colSpan ?? 1); s++) {
						occupied.add(`${c + s},${r}`);
					}
					if (r >= maxRow) maxRow = r + 1;
					return { ...f, col: c, row: r };
				}
			}
		}
	});

	return {
		...tab,
		columns,
		rows: Math.max(maxRow, ...fields.map((f) => (f.row ?? 0) + 1)),
		fields,
	};
}
